import * as _ from 'lodash';
import {AccountPreferredAgent} from '../models/account-preferred-agent.model';
import {UpsertMultipleAccountPreferredAgentsRequest} from '../models/account-with-preferred-agents.model';

export interface AccountAgentCategoryIds {
  primaryIds: string[];
  secondaryIds: string[];
  dedicatedIds: string[];
  restrictedIds: string[];
}

export type AccountAgentCategory = 'primary' | 'secondary' | 'dedicated' | 'restricted';

/**
 * Minimal shape needed to render an <agent-link> (agent-id + agent-name) without
 * having to resolve the full AgentModel. id is optional - an agent with no resolvable
 * id still has a name worth displaying, it just can't be rendered as a link.
 */
export interface AgentLinkItem {
  id?: string;
  name: string;
}

export function sanitizeAccountAgentCategoryIds(categoryIds: AccountAgentCategoryIds): AccountAgentCategoryIds {
  const primaryIds = categoryIds.primaryIds || [];
  const secondaryIds = categoryIds.secondaryIds || [];
  const dedicatedIds = categoryIds.dedicatedIds || [];
  const restrictedIds = categoryIds.restrictedIds || [];

  return {
    primaryIds,
    secondaryIds: _.difference(secondaryIds, primaryIds),
    dedicatedIds: _.difference(dedicatedIds, restrictedIds),
    restrictedIds
  };
}

export function applyAccountAgentCategoryChange(
  categoryIds: AccountAgentCategoryIds,
  category: AccountAgentCategory,
  ids: string[]
): AccountAgentCategoryIds {
  const next: AccountAgentCategoryIds = {
    primaryIds: categoryIds.primaryIds || [],
    secondaryIds: categoryIds.secondaryIds || [],
    dedicatedIds: categoryIds.dedicatedIds || [],
    restrictedIds: categoryIds.restrictedIds || []
  };
  const updatedIds = ids || [];

  switch (category) {
    case 'primary':
      next.primaryIds = updatedIds;
      next.secondaryIds = _.difference(next.secondaryIds, updatedIds);
      break;
    case 'secondary':
      next.secondaryIds = updatedIds;
      next.primaryIds = _.difference(next.primaryIds, updatedIds);
      break;
    case 'dedicated':
      next.dedicatedIds = updatedIds;
      next.restrictedIds = _.difference(next.restrictedIds, updatedIds);
      break;
    case 'restricted':
      next.restrictedIds = updatedIds;
      next.dedicatedIds = _.difference(next.dedicatedIds, updatedIds);
      break;
  }

  return next;
}

export function isRestrictedAgent(agent: AccountPreferredAgent): boolean {
  return agent.restricted === true || agent.isRestricted === true || agent.category === 'restricted';
}

export function isDedicatedAgent(agent: AccountPreferredAgent): boolean {
  return agent.dedicated === true || agent.isDedicated === true || agent.category === 'dedicated';
}

export function isSecondaryAgent(agent: AccountPreferredAgent): boolean {
  return agent.category === 'secondary' || agent.priority === 2;
}

export function isPrimaryAgent(agent: AccountPreferredAgent): boolean {
  return agent.category === 'primary' || agent.priority === 1;
}

export function categorizePreferredAgents(agents: AccountPreferredAgent[], serviceType?: string): AccountAgentCategoryIds {
  const filtered = serviceType
    ? _.filter(agents, (agent) => agent.serviceType === serviceType)
    : agents;

  const primaryIds: string[] = [];
  const secondaryIds: string[] = [];
  const dedicatedIds: string[] = [];
  const restrictedIds: string[] = [];

  _.forEach(filtered, (agent) => {
    const agentId = getAgentId(agent);
    if (!agentId) {
      return;
    }

    if (isRestrictedAgent(agent)) {
      restrictedIds.push(agentId);
    }
    if (isDedicatedAgent(agent)) {
      dedicatedIds.push(agentId);
    }
    if (isSecondaryAgent(agent)) {
      secondaryIds.push(agentId);
    }
    if (isPrimaryAgent(agent)) {
      primaryIds.push(agentId);
    }
  });

  return {
    primaryIds: _.uniq(primaryIds),
    secondaryIds: _.uniq(secondaryIds),
    dedicatedIds: _.uniq(dedicatedIds),
    restrictedIds: _.uniq(restrictedIds)
  };
}

export function buildAssignMultiplePayload(
  serviceType: string,
  categoryIds: AccountAgentCategoryIds
): UpsertMultipleAccountPreferredAgentsRequest {
  const sanitized = sanitizeAccountAgentCategoryIds(categoryIds);
  return {
    serviceType,
    primaryIds: sanitized.primaryIds || [],
    secondaryIds: sanitized.secondaryIds || [],
    dedicatedIds: sanitized.dedicatedIds || [],
    restrictedIds: sanitized.restrictedIds || []
  };
}

export function getAgentId(agent: AccountPreferredAgent): string {
  return agent.agentId || _.get(agent, 'agentReference.id');
}

export function getAgentDisplayName(agent: AccountPreferredAgent): string {
  return _.get(agent, 'agentReference.fullName')
    || _.get(agent, 'agentReference.name')
    || agent.agentName
    || getAgentId(agent);
}

/**
 * agent names in the assignment table are rendered as links to the agent
 * profile, so each cell needs {id, name} pairs rather than a single joined string.
 * An agent is only dropped if it has neither an id nor a name to show - anything
 * with at least one of those is kept, matching the previous plain-text rendering.
 */
export function toAgentLinkItems(agents: AccountPreferredAgent[]): AgentLinkItem[] {
  return _.compact(_.map(agents, (agent) => {
    const id = getAgentId(agent);
    const name = getAgentDisplayName(agent);
    return (id || name) ? {id, name} : undefined;
  }));
}

export function filterAgentsByCategory(
  agents: AccountPreferredAgent[],
  serviceType: string,
  category: 'primary' | 'secondary' | 'dedicated' | 'restricted'
): AccountPreferredAgent[] {
  const filtered = _.filter(agents, (agent) => agent.serviceType === serviceType);

  switch (category) {
    case 'restricted':
      return _.filter(filtered, isRestrictedAgent);
    case 'dedicated':
      return _.filter(filtered, isDedicatedAgent);
    case 'secondary':
      return _.filter(filtered, isSecondaryAgent);
    case 'primary':
    default:
      return _.filter(filtered, isPrimaryAgent);
  }
}
